import { apiHelpFeedback } from "@/api/help-feedback.api.ts";
import { Breadcrumbs } from "@/components/breadcrumbs/Breadcrumbs.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { HelpArticleFeedback } from "@/pages/help/components/HelpArticleFeedback.tsx";
import { mdxComponents } from "@/pages/help/components/mdx-components.tsx";

import { useHelp } from "@/pages/help/help.hook.tsx";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { DEFAULT_HELP_LANGUAGE, resolveHelpLanguage } from "@/utils/help-language.ts";
import { compile, run } from "@mdx-js/mdx";
import { MDXProvider } from "@mdx-js/react";
import { Box, Page, Skeleton, Stack, TableOfContents, Tag, Text, Title } from "@skailer-com/uikit";
import { MDXComponents } from "mdx/types";
import { ComponentType, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useEffectOnce } from "react-use";
import * as runtime from "react/jsx-runtime";
import remarkGfm from "remark-gfm";

export const HelpArticle = () => {
  const { t, i18n } = useTranslation();
  const { slug } = useParams<{
    slug: string;
  }>();

  const language = resolveHelpLanguage(i18n.resolvedLanguage || i18n.language);
  const [Content, setContent] = useState<ComponentType<{ components?: MDXComponents }> | null>(
    null
  );

  const { helpIndex, loadContent, findArticleBySlug, findCategoryBySlug, getCategoryPath } =
    useHelp(language);

  const currentArticle = useMemo(() => {
    if (!helpIndex) {
      return null;
    }

    return findArticleBySlug(slug || "");
  }, [findArticleBySlug, helpIndex, slug]);

  const currentCategory = useMemo(() => {
    if (!currentArticle) {
      return null;
    }

    return findCategoryBySlug(currentArticle.category);
  }, [currentArticle, findCategoryBySlug]);

  useTitle(currentArticle?.title, currentCategory?.title, t("common:help"));

  const categoriesPath = useMemo(() => {
    if (!currentCategory) return [];
    return getCategoryPath(currentCategory);
  }, [currentCategory, getCategoryPath]);

  const breadcrumbItems = useMemo(() => {
    if (!currentCategory) {
      return [];
    }

    return [
      ...categoriesPath.map((item) => ({
        id: item.id,
        title: item.title,
        to: item.url,
      })),
      ...(currentArticle
        ? [
            {
              id: currentArticle.id,
              title: currentArticle.title,
            },
          ]
        : []),
    ];
  }, [categoriesPath, currentArticle, currentCategory]);

  const showAiTranslationNotice =
    language !== DEFAULT_HELP_LANGUAGE && currentArticle?.sourceLanguage !== DEFAULT_HELP_LANGUAGE;

  const showOriginalLanguageNotice =
    language !== DEFAULT_HELP_LANGUAGE && currentArticle?.sourceLanguage === DEFAULT_HELP_LANGUAGE;

  useEffect(() => {
    if (!currentArticle) return;

    const loadItemContent = async () => {
      try {
        if (currentArticle) {
          const loadedContent = await loadContent(
            currentArticle.file,
            currentArticle.sourceLanguage
          );
          const compiled = await compile(loadedContent, {
            outputFormat: "function-body",
            development: false,
            remarkPlugins: [remarkGfm],
          });
          const { default: mdxContent } = await run(compiled, runtime);
          setContent(() => mdxContent);
        }
      } catch (error) {
        console.error("Failed to load content:", error);
      }
    };

    loadItemContent();
  }, [loadContent, currentArticle]);

  const currentArticleId = currentArticle?.id;

  useEffect(() => {
    if (currentArticleId === undefined) return;
    void apiHelpFeedback.registerView({ article_id: currentArticleId, language });
  }, [currentArticleId, language]);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.help.articleViewed, {
      [analyticProperties.id]: slug,
    });
  });

  return (
    <>
      <Page>
        {currentCategory && (
          <Page.Header
            showBack={true}
            navigateTo={`/help/collection/${currentCategory.id}`}
            backSlot={<Breadcrumbs items={breadcrumbItems} separator={"·"} />}
          ></Page.Header>
        )}
        <Page.Content>
          <Stack className={"py-10 px-16 gap-10"}>
            {!currentArticle && <Skeleton className={"w-full h-10"} />}
            {currentArticle && Content && (
              <Box>
                <Title size={"xl"} header={currentArticle.title} paddingBottom={true} />
                {currentArticle.description && (
                  <Text size={"lg"}>{currentArticle.description}</Text>
                )}
                {showAiTranslationNotice && (
                  <Stack direction={"horizontal"} className={"mt-6"}>
                    <Tag variant={"new"} appearance={"filled"}>
                      {t("common:help_ai_translation_notice")}
                    </Tag>
                  </Stack>
                )}
                {showOriginalLanguageNotice && (
                  <Stack direction={"horizontal"} className={"mt-6"}>
                    <Tag variant={"new"} appearance={"filled"}>
                      {t("common:help_original_language_notice")}
                    </Tag>
                  </Stack>
                )}
                <div className={"grid flex-1 md:grid-cols-[1fr_330px] pt-(--gap-3xl)"}>
                  <article
                    className={
                      "flex w-full flex-col overflow-hidden html-content html-content-large html-content-help"
                    }
                  >
                    <MDXProvider>
                      <Content components={mdxComponents} />
                    </MDXProvider>
                    <HelpArticleFeedback articleId={currentArticle.id} language={language} />
                  </article>
                  <div className={"flex flex-col w-full pl-12"}>
                    <div className={"sticky top-0 z-10 flex flex-col w-full gap-xl"}>
                      <TableOfContents headingLevels={[2]} scrollContainer={"main"} />
                    </div>
                  </div>
                </div>
              </Box>
            )}
          </Stack>
        </Page.Content>
      </Page>
    </>
  );
};
